/**
 * This installation's identity, and the per-company registry of the devices
 * that have written to a bookkeeping.
 *
 * Three rules shape everything here:
 *
 *  - The identity is *local*. It lives on the settings row, which no snapshot
 *    carries, so a restored copy on another machine mints its own id instead
 *    of claiming to be the machine that wrote the backup. Two browser profiles
 *    on one computer are two devices, and that is the honest answer.
 *  - Durable records store the **id**, never the name. A name is editable, so
 *    a name copied into an append-only verifikation would freeze whatever the
 *    device happened to be called that day. The registry resolves ids to names
 *    and may be rewritten freely.
 *  - The registry is working state, like `inboxItems` or `merchantRules`, and
 *    is deliberately not one of the scheduler's `HOOKED_TABLES`: seeing a
 *    device today is not a bookkeeping change and must not trigger a backup of
 *    its own. It rides along with the next real one.
 */
import { db as defaultDb, type BookkeepingDB } from '@/db/db';
import { getSettings, updateSettings } from '../settings/service';
import type { DeviceIdentity, DeviceRegistration, IsoDate, Uuid } from '../types';
import { normalizeDeviceName, suggestDeviceName, type DeviceNameHints } from './name';

/**
 * Held in memory so the booking path can stamp `deviceId` synchronously.
 * `bookVerifikation` runs inside a Dexie transaction whose table scope is
 * fixed by its callers; reading the settings row there would force every
 * caller to widen its scope, and a booking must never fail over a label.
 */
let cached: DeviceIdentity | null = null;

export interface DeviceDeps {
  /** Suggested name for a brand new identity. */
  suggestName: () => string;
  now: () => Date;
}

export const defaultDeviceDeps: DeviceDeps = {
  suggestName: () => suggestDeviceName(readDeviceNameHints()),
  now: () => new Date(),
};

/** What `navigator` will admit to, if there is one. Empty in node and tests. */
export function readDeviceNameHints(): DeviceNameHints {
  const nav = typeof navigator === 'undefined' ? undefined : (navigator as Navigator & { userAgentData?: { platform?: string; mobile?: boolean; brands?: { brand: string }[] } });
  if (!nav) return {};
  const data = nav.userAgentData;
  return {
    platform: data?.platform ?? nav.platform,
    brands: data?.brands,
    userAgent: nav.userAgent,
    ...(data?.mobile === undefined ? {} : { mobile: data.mobile }),
  };
}

/** Same day boundary as `swedishDate`, without pulling in the reports layer. */
function today(now: Date): IsoDate {
  return new Intl.DateTimeFormat('sv-SE', { timeZone: 'Europe/Stockholm' }).format(now);
}

/**
 * The identity, minted and persisted on first call. Call it once at start-up
 * (`wireApp`) so everything after it can read the cache synchronously.
 */
export async function ensureDeviceIdentity(db: BookkeepingDB = defaultDb, deps: DeviceDeps = defaultDeviceDeps): Promise<DeviceIdentity> {
  const existing = (await getSettings(db)).device;
  if (existing) {
    cached = existing;
    return existing;
  }
  const device: DeviceIdentity = {
    id: crypto.randomUUID(),
    name: normalizeDeviceName(deps.suggestName()),
    createdAt: deps.now().toISOString(),
  };
  await updateSettings({ device }, db);
  cached = device;
  return device;
}

/** The identity if there is one, without creating it. Caches what it finds. */
export async function loadDeviceIdentity(db: BookkeepingDB = defaultDb): Promise<DeviceIdentity | null> {
  const device = (await getSettings(db)).device;
  if (device) cached = device;
  return device;
}

/** Synchronous, for the booking path. Undefined until an identity is established. */
export function currentDeviceId(): Uuid | undefined {
  return cached?.id;
}

/** Synchronous, for anything that wants to show the local name. */
export function currentDevice(): DeviceIdentity | null {
  return cached;
}

/** Tests establish their own identity per database; the cache must not leak between them. */
export function resetDeviceIdentityCache(): void {
  cached = null;
}

/**
 * Rename this device. The name is a label: every registry row this device owns
 * follows it, and nothing already booked changes, because verifikationer carry
 * the id alone.
 */
export async function renameDevice(name: string, db: BookkeepingDB = defaultDb, deps: DeviceDeps = defaultDeviceDeps): Promise<DeviceIdentity> {
  const current = await ensureDeviceIdentity(db, deps);
  const device: DeviceIdentity = { ...current, name: normalizeDeviceName(name) };
  if (device.name === current.name) return current;
  await updateSettings({ device }, db);
  cached = device;
  const rows = await db.devices.where('deviceId').equals(device.id).toArray();
  await db.devices.bulkPut(rows.map((row) => ({ ...row, name: device.name })));
  return device;
}

export function registrationId(companyId: Uuid, deviceId: Uuid): string {
  return `${companyId}:${deviceId}`;
}

/**
 * Note that this device is one of the company's. Idempotent, and writes only
 * when the name or the day actually changed, so opening the app a second time
 * on the same day touches nothing.
 */
export async function registerDevice(companyId: Uuid, db: BookkeepingDB = defaultDb, deps: DeviceDeps = defaultDeviceDeps): Promise<DeviceRegistration> {
  const device = await ensureDeviceIdentity(db, deps);
  const day = today(deps.now());
  const id = registrationId(companyId, device.id);
  const existing = await db.devices.get(id);
  if (existing && existing.name === device.name && existing.lastSeen === day) return existing;
  const row: DeviceRegistration = {
    id,
    companyId,
    deviceId: device.id,
    name: device.name,
    firstSeen: existing?.firstSeen ?? day,
    lastSeen: day,
  };
  await db.devices.put(row);
  return row;
}

/** Every device known to this company, this one first, then by name. */
export async function listDevices(companyId: Uuid, db: BookkeepingDB = defaultDb): Promise<DeviceRegistration[]> {
  const rows = await db.devices.where('companyId').equals(companyId).toArray();
  const mine = currentDeviceId();
  return rows.sort((a, b) => (a.deviceId === mine ? -1 : b.deviceId === mine ? 1 : a.name.localeCompare(b.name, 'sv')));
}

/** `deviceId` -> name, for a report or a list that shows many at once. */
export async function deviceNames(companyId: Uuid, db: BookkeepingDB = defaultDb): Promise<Map<Uuid, string>> {
  return new Map((await listDevices(companyId, db)).map((d) => [d.deviceId, d.name]));
}
